import { shallowEqual, type TypedUseSelectorHook, useDispatch as useAppDispatch, useSelector as useAppSelector } from 'react-redux';
import { experimentsReducer as experiments } from '@alltrails/experiments';
import lists, { type ListsState } from '@alltrails/redux/slices/lists';
import userConnections from '@alltrails/redux/slices/userConnections';
import userConnectionLists from '@alltrails/redux/slices/userConnectionLists';
import type Context from '@alltrails/core/types/Context';
import context from '@alltrails/context/slices/contextSlice';
import currencyCodeReducer from '@alltrails/context/slices/currencyCodeSlice';
import plans from '@alltrails/context/slices/plansSlice';
import features from '@alltrails/features/slices/features';
import amplitudeAnalyticsSettings from '@alltrails/amplitude/slices/amplitudeAnalyticsSettings';
import { configureStore } from '@reduxjs/toolkit';
import { waypoints, type WaypointsState } from '@alltrails/waypoints/slice';
import { baseApi } from '@alltrails/redux-helpers';
import reviewsSearchFilters from '@alltrails/reviews/slices/reviewsSearchFilters';
import { MapState, mapReducer } from '@alltrails/maps';
import { heatmapsReducer, HeatmapsState } from '@alltrails/heatmaps/redux/heatmapsSlice';
import { PhotoTourState, photoTourReducer } from '@alltrails/photo-tour';
import { DiToolingState, diToolingReducer } from '@alltrails/di-tooling';
import { AlgoliaConfigs } from '@alltrails/types/algoliaConfigs';
import algoliaConfigs from '@alltrails/redux/slices/algoliaConfigs';
import installBanner, { InstallBannerState } from '@alltrails/install-prompt/slices/installBanner';
import type CountryCode from '@alltrails/core/types/CountryCode';
import type RequiredPreloadedState from '@alltrails/core/types/RequiredPreloadedState';
import { conditionsReducer, ConditionsState } from '@alltrails/conditions/redux/conditionsSlice';
import conditionsMiddleware from '@alltrails/conditions/redux/conditionsMiddleware';
import { type FlyoverState, flyoverReducer } from '@alltrails/flyover/redux/flyover';
import { listenerMiddleware } from './listenerMiddleware';
import trailPage, { TrailPageState } from './trailPageSlice';

export type PreloadedState = RequiredPreloadedState &
  Partial<{
    algoliaConfigs: AlgoliaConfigs;
    conditions: ConditionsState;
    diTooling: DiToolingState;
    flyover: FlyoverState;
    heatmaps: HeatmapsState;
    installBanner: InstallBannerState;
    lists: ListsState;
    map: MapState;
    photoTour: PhotoTourState;
    trailPage: TrailPageState;
    waypoints: WaypointsState;
  }>;

export const reducer = {
  [baseApi.reducerPath]: baseApi.reducer,
  algoliaConfigs,
  amplitudeAnalyticsSettings,
  browser: (state = {}) => state,
  conditions: conditionsReducer,
  context,
  currencyCode: currencyCodeReducer,
  diTooling: diToolingReducer,
  experiments,
  features,
  flyover: flyoverReducer,
  heatmaps: heatmapsReducer,
  installBanner,
  irclickid: (state = '') => state,
  lists,
  map: mapReducer,
  photoTour: photoTourReducer,
  plans,
  requestCountryCode: (state = '' as CountryCode) => state,
  reviewsSearchFilters,
  trailPage,
  userConnectionLists,
  userConnections,
  waypoints
};

export const buildStore = (preloadedState?: PreloadedState) =>
  configureStore({
    reducer,
    preloadedState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({ immutableCheck: false }).prepend(listenerMiddleware.middleware).concat(baseApi.middleware).concat(conditionsMiddleware)
  });

export type AppStore = ReturnType<typeof buildStore>;
export type AppDispatch = AppStore['dispatch'];
export type RootState = ReturnType<AppStore['getState']> & { context?: Context };

// these are intended to use within the app only
export const useDispatch = (): AppDispatch => useAppDispatch<AppDispatch>();
export const useSelector: TypedUseSelectorHook<RootState> = (selector: (state: RootState) => any, equalityFn = shallowEqual) =>
  useAppSelector<RootState, any>(selector, equalityFn);
